/**
 * Email signup shown in place of the buy button on sold-out products.
 *
 * Two jobs:
 *  1. Reveal the inline email row when the button is clicked.
 *  2. Post to Shopify's own /contact customer form in the background, so the
 *     subscriber is saved without a page reload.
 *
 * Visibility follows the selected variant. It listens to the theme's existing
 * variantChange event rather than re-rendering, so switching size keeps the
 * signup and the buy button in sync.
 */
class EmailSignup extends HTMLElement {
  constructor() {
    super();

    this.openButton = this.querySelector("[data-email-signup-open]");
    this.formWrapper = this.querySelector("[data-email-signup-form]");
    this.message = this.querySelector("[data-email-signup-message]");
    this.form = this.querySelector("form");

    this.openButton?.addEventListener("click", () => this.reveal());
    this.form?.addEventListener("submit", (event) => this.onSubmit(event));
  }

  reveal() {
    this.openButton.hidden = true;
    this.formWrapper.hidden = false;
    this.form?.querySelector('input[type="email"]')?.focus();
  }

  async onSubmit(event) {
    event.preventDefault();

    const input = this.form.querySelector('input[type="email"]');
    if (!input?.value || !input.checkValidity()) {
      this.showMessage(this.dataset.messageInvalid, true);
      return;
    }

    const submitButton = this.form.querySelector('[type="submit"]');
    submitButton?.setAttribute("disabled", "disabled");

    try {
      const response = await fetch(this.form.action, {
        method: "POST",
        body: new FormData(this.form),
        headers: { Accept: "text/html" },
      });

      // Shopify redirects to ?customer_posted=true on a successful signup.
      // Anything else means it rejected the address.
      if (response.ok && response.url.includes("customer_posted=true")) {
        this.formWrapper.hidden = true;
        this.showMessage(this.dataset.messageSuccess, false);
      } else {
        this.showMessage(this.dataset.messageInvalid, true);
        submitButton?.removeAttribute("disabled");
      }
    } catch (error) {
      console.error("Email signup failed", error);
      this.showMessage(this.dataset.messageError, true);
      submitButton?.removeAttribute("disabled");
    }
  }

  showMessage(text, isError) {
    if (!this.message) return;
    this.message.textContent = text || "";
    this.message.hidden = !text;
    this.message.classList.toggle("email-signup__message--error", !!isError);
  }

  /** Reset to the collapsed state when the customer changes size. */
  reset() {
    if (this.openButton) this.openButton.hidden = false;
    if (this.formWrapper) this.formWrapper.hidden = true;
    this.showMessage("", false);
    this.form
      ?.querySelector('[type="submit"]')
      ?.removeAttribute("disabled");
  }
}

customElements.define("email-signup", EmailSignup);

// Swap between the buy button and the signup as the selected variant changes.
if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const sectionId = event?.data?.sectionId;
    const variant = event?.data?.variant;
    if (!sectionId) return;

    const signup = document.getElementById(`EmailSignup-${sectionId}`);
    if (!signup) return;

    const buyable = Boolean(variant && variant.available);
    signup.classList.toggle("hidden", buyable);
    document
      .getElementById(`BuyButtons-${sectionId}`)
      ?.classList.toggle("hidden", !buyable);

    if (!buyable) signup.reset?.();
  });
}
